import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { useRouter } from "next/router";
import { User } from "@/types/login";

type AuthContextType = {
  user: User | null;
  login: (
    username: string,
    password: string
  ) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);

      const fetchIsAbsen = async () => {
        try {
          const response = await fetch(
            "https://tarum-tools.aurorasystem.co.id/kepegawaian-api/pwa/check-akun",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                pgw_id: parsedUser.pgw_id,
              }),
            }
          );

          const result = await response.json();

          if (result.is_absen_webview === "0") {
            localStorage.removeItem("user");
            setUser(null);
            router.replace("/masuk-akun");
          }
        } catch (error) {
          console.error("Gagal cek is_absen_webview:", error);
        }
      };

      fetchIsAbsen();
    }

    setLoading(false);
  }, []);

  const login = async (
    username: string,
    password: string
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await fetch(
        "https://tarum-tools.aurorasystem.co.id/kepegawaian-api/pwa/login-pwa",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ username, password }),
        }
      );

      const result = await response.json();
      console.log(result);
      if (result.stts === "1") {
        setUser(result);
        localStorage.setItem("user", JSON.stringify(result));
        router.push("/");
        return { success: true };
      } else {
        return {
          success: false,
          message: result.msg,
        };
      }
    } catch (err) {
      console.error("Login error:", err);
      return {
        success: false,
        message: "Something went wrong, please try again.",
      };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("user");
    router.push("/masuk-akun");
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{ user, login, logout, isAuthenticated, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
